import React, { useState } from "react";

import { useMutation } from "@apollo/client";

import MarketingLink from "@/components/common/LinkHandler/MarketingLink";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { getSegmentAnonymousId } from "@/components/vendor/Analytics";
import { emailSubscribeMutation } from "@/queries"; // Define this GraphQL mutation in a .ts file
import { GA4 } from "@/utils/ga4Utils";
import { cn, isEmailValid } from "@/utils/general";
import { ScreenReader } from "@/utils/ScreenReader";
import { identifyEvent } from "@/utils/segmentfunctions";
import { segmentEvent } from "@/utils/segmentfunctions";
import { ThemeTypes } from "@/utils/theming";

import styles from "./Footer.module.scss";

const MESSAGES = {
  SUCCESS: "Thanks for signing up!",
  ERROR: "Something went wrong. ",
  ERROR2: "Could not connect to the registration server. Please try again later.",
  ERROR3: "There was a problem with the subscription: ",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const EmailSignup: React.FC<{ data: any; type: string; theme: ThemeTypes }> = ({
  data,
  type,
  theme,
}) => {
  const [emailError, setEmailError] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [subscribeInProgress, setSubscribeInProgress] = useState<boolean>(false);
  const [subscribeForNewsletters] = useMutation(emailSubscribeMutation);
  const isMen = theme === "men";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  const handleEmailError = (failure_reason: string) => {
    segmentEvent("Email Signup Failed", {
      newsletter_title: "Nutrafol Newsletter",
      failure_reason,
      type: "footer",
      location: isMen ? "men_footer" : "women_footer",
      business_line: "Hair",
      product_line: isMen ? "men" : "women",
    });
    setEmailError(failure_reason);
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (subscribeInProgress) {
      return;
    }
    if (inputValue === "") {
      handleEmailError("This is a required field.");
      return;
    }
    if (!isEmailValid(inputValue)) {
      handleEmailError("Please enter a valid email address (Ex: johndoe@domain.com).");
      return;
    }

    setSubscribeInProgress(true);
    setEmailSent(true);
    setEmailError(""); // Clear previous errors

    segmentEvent("Email Signup Started", {
      newsletter_title: "Nutrafol Newsletter",
      type: "footer",
      location: isMen ? "men_footer" : "women_footer",
      business_line: "Hair",
      product_line: isMen ? "men" : "women",
    });

    try {
      // Execute the GraphQL mutation
      const { data: resData } = await subscribeForNewsletters({
        variables: {
          email: inputValue,
        },
      });

      const result = resData?.subscribeForNewsletters?.result;
      const status = parseInt(result?.status || "0", 10); // Parse status as integer
      const content = result?.message || "";

      if (status === 403) {
        handleEmailError(MESSAGES.ERROR3 + content);
        ScreenReader.speak(MESSAGES.ERROR3);
      } else if (status === 200) {
        setEmailError("");
        ScreenReader.speak(MESSAGES.SUCCESS);
        GA4.onEmailMarketingOptedIn({
          anonymousId: getSegmentAnonymousId(),
          email: inputValue,
          form_type: "footer form",
          gender: theme === "men" ? "men" : "women",
          acquisition_source: `${theme}_footer`,
        });
        identifyEvent({
          email: inputValue,
          email_marketing_opted_in: true,
        });
      } else {
        handleEmailError(MESSAGES.ERROR + MESSAGES.ERROR2);
        ScreenReader.speak(MESSAGES.ERROR3);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Email subscription error:", error);
      handleEmailError(MESSAGES.ERROR + MESSAGES.ERROR2);
      ScreenReader.speak(MESSAGES.ERROR3);
    } finally {
      setSubscribeInProgress(false);
    }
  };

  return (
    <form onSubmit={handleEmailSubmit} className={cn(styles.formEmailAddress, type)}>
      <TextElementNova className="Body-10-Dia-Normal" text={data.form.text} />

      {!subscribeInProgress && emailSent && !emailError ? (
        <TextElementNova className="Body-10-Dia-Strong" text={MESSAGES.SUCCESS} />
      ) : (
        <div className={cn(styles.formGroup, "form-group")}>
          <input
            type="text"
            name="email"
            onChange={handleInputChange}
            value={inputValue}
            className={cn(styles.formEmail, "form-email")}
            placeholder={data.form.placeholder}
            aria-label="Email"
          />
          <button
            type="submit"
            className={cn(styles.formSubmit, "form-submit")}
            aria-label="Submit"
          >
            <ArrowRight />
          </button>
        </div>
      )}

      {emailError && (
        <div className={styles.emailError}>
          <TextElementNova className="Caption-10-Dia-Normal" text={emailError} />
        </div>
      )}

      <p className={styles.signUpTerms}>
        <TextElementNova text="By signing up, you agree to our " element="span" />
        <MarketingLink
          path="/our/brand/support/legal/signupsave-terms/"
          module_location={`${theme}_footer`}
        >
          Sign-Up and Save Policy
        </MarketingLink>
        <TextElementNova text=" and our  " element="span" />
        <MarketingLink
          path="/our/brand/support/privacy-policy/"
          module_location={`${theme}_footer`}
        >
          Privacy Policy
        </MarketingLink>
        <TextElementNova text="." element="span" />
      </p>
    </form>
  );
};
